type Hsl = { h: number; s: number; l: number };

const clamp = (v: number, min = 0, max = 100) =>
	Math.max(min, Math.min(max, v));
const css = ({ h, s, l }: Hsl) =>
	`hsl(${h.toFixed(1)}, ${s.toFixed(0)}%, ${l.toFixed(0)}%)`;

function hslToRgb({ h, s, l }: Hsl): [number, number, number] {
	const S = s / 100;
	const L = l / 100;
	const k = (n: number) => (n + h / 30) % 12;
	const a = S * Math.min(L, 1 - L);
	const f = (n: number) =>
		L - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
	return [f(0) * 255, f(8) * 255, f(4) * 255];
}

function luminance(rgb: [number, number, number]): number {
	const [r, g, b] = rgb.map((c) => {
		const v = c / 255;
		return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
	});
	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a: Hsl, b: Hsl): number {
	const la = luminance(hslToRgb(a));
	const lb = luminance(hslToRgb(b));
	const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
	return (hi + 0.05) / (lo + 0.05);
}

function hexToHsl(hex: string): Hsl | null {
	const normalized = hex.trim().replace(/^#/, "");
	const full =
		normalized.length === 3
			? normalized
					.split("")
					.map((c) => c + c)
					.join("")
			: normalized;
	if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;

	const r = Number.parseInt(full.slice(0, 2), 16) / 255;
	const g = Number.parseInt(full.slice(2, 4), 16) / 255;
	const b = Number.parseInt(full.slice(4, 6), 16) / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const delta = max - min;
	const l = (max + min) / 2;

	if (delta === 0) return { h: 0, s: 0, l: l * 100 };

	const s = delta / (1 - Math.abs(2 * l - 1));
	let h: number;
	if (max === r) h = ((g - b) / delta) % 6;
	else if (max === g) h = (b - r) / delta + 2;
	else h = (r - g) / delta + 4;
	h *= 60;
	if (h < 0) h += 360;

	return { h, s: s * 100, l: l * 100 };
}

export function getReadableTextColor(
	background: string,
	minContrast = 4.5,
	maxSaturation = 90,
): string {
	const bg = hexToHsl(background);
	if (!bg) return "#000000";

	const textSaturation = clamp(bg.s + 25, 0, maxSaturation);
	for (let l = Math.round(bg.l) - 10; l >= 0; l--) {
		const candidate: Hsl = { h: bg.h, s: textSaturation, l };
		if (contrast(bg, candidate) >= minContrast) return css(candidate);
	}

	const white: Hsl = { h: 0, s: 0, l: 100 };
	const black: Hsl = { h: 0, s: 0, l: 0 };
	return contrast(bg, white) >= contrast(bg, black) ? "#FFFFFF" : "#000000";
}

export type SectionColors = {
	fill: string;
	border: string;
	text: string;
	highlighted: string;
};

const cache = new Map<number, SectionColors>();

export function getColors(rawId: number | string): SectionColors {
	const parsed = Number(rawId);
	const id = Number.isFinite(parsed) ? Math.trunc(parsed) : 0;

	const cached = cache.get(id);
	if (cached) return cached;

	const built = buildColors(id);
	cache.set(id, built);
	return built;
}

function buildColors(id: number): SectionColors {
	const goldenRatio = 0.618033988749;
	const hue = (((id * goldenRatio * 360) % 360) + 360) % 360;
	const saturation = 40 + (Math.abs(id * 17) % 20);
	const lightness = 70 + (Math.abs(id * 13) % 15);

	const bg: Hsl = { h: hue, s: saturation, l: lightness };

	const textSaturation = clamp(saturation + 25, 0, 90);

	let text: Hsl = { h: hue, s: textSaturation, l: 5 };
	for (let l = Math.round(lightness) - 10; l >= 5; l--) {
		const candidate: Hsl = { h: hue, s: textSaturation, l };
		if (contrast(bg, candidate) >= 4.5) {
			text = candidate;
			break;
		}
	}

	return {
		fill: css(bg),
		border: css({
			h: hue,
			s: clamp(saturation + 10),
			l: clamp(lightness - 20),
		}),
		highlighted: css({
			h: hue,
			s: clamp(saturation + 20),
			l: clamp(lightness - 15),
		}),
		text: css(text),
	};
}
